import React, { useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * A notes box edited in place. `onSave(text)` does the API call; the parent
 * refreshes its data afterwards. Shows "Add a note" when empty and editable.
 */
export default function NotesEditor({ value, onSave, editable = true, placeholder, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(text.trim());
      toast.success('Notes saved');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the notes');
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className={`max-w-xl ${className}`}>
        <label className="block text-[11px] font-semibold text-amber-700 uppercase mb-1">Notes</label>
        <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} autoFocus
          placeholder={placeholder || 'Anything the office or a driver should know…'}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <div className="flex gap-2 mt-1.5">
          <button onClick={save} disabled={busy}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save notes'}
          </button>
          <button onClick={() => { setEditing(false); setText(value || ''); }} className="px-3 py-1.5 rounded-lg text-xs border text-gray-600">Cancel</button>
        </div>
      </div>
    );
  }
  if (!value) {
    return editable ? (
      <button onClick={() => { setText(''); setEditing(true); }} className={`inline-flex items-center gap-1 text-xs text-gray-400 hover:text-orange-600 ${className}`}>
        <Plus size={12} /> Add a note
      </button>
    ) : null;
  }
  return (
    <div className={`bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-xl ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <p className="text-[11px] font-semibold text-amber-700 uppercase">Notes</p>
        {editable && (
          <button onClick={() => { setText(value || ''); setEditing(true); }} title="Edit notes"
            className="text-amber-600 hover:text-amber-800"><Pencil size={13} /></button>
        )}
      </div>
      <p className="text-sm text-amber-900 whitespace-pre-wrap">{value}</p>
    </div>
  );
}
